import { useCallback } from "react";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import usePagination from "src/customHooks/usePagination";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";
import { WordBankItem } from "src/features/ai-sessions/types";
import { WordBankSearch } from "../components/word-bank-search";
import { WordBankTable } from "../components/word-bank-table";
import { WordBankTableSkeleton } from "../components/WordBankTableSkeleton";
import { useCountWordBankQuery, useGetWordBankQuery, WordBankFilter } from "../word-bank.service";

const WordBankPage = () => {
  const tableSearch = usePaginatedTableSearchParams<WordBankFilter>({
    parseState: (searchParams) => {
      const query: WordBankFilter = {};
      const id = searchParams.get("id");
      const userId = searchParams.get("userId");
      if (id) query.id = id;
      if (userId) query.userId = userId;
      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.id) nextParams.set("id", state.id);
      if (state.userId) nextParams.set("userId", state.userId);
      return nextParams;
    },
  });

  const initialQuery = tableSearch.state;

  const {
    data,
    numberOfPages,
    currentPage,
    next,
    prev,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    error,
  } = usePagination<WordBankItem>({
    useGetDataQuery: useGetWordBankQuery,
    useCountData: useCountWordBankQuery,
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: tableSearch.syncKey,
    onPaginationStateChange: tableSearch.replacePagination,
  });

  const updateQuery = useCallback(
    (newQuery: WordBankFilter) => {
      tableSearch.replaceFilters(newQuery);
    },
    [tableSearch]
  );

  const errorMessage =
    error && typeof error === "object" && error !== null && "data" in error
      ? String(error.data)
      : "There was an error fetching the word bank data. Please check the console for more details.";
  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Word Bank" />

      <div className="mb-4">
        <WordBankSearch updateQuery={updateQuery} initialQuery={initialQuery} />
      </div>

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
              <table className="min-w-[1820px] sm:min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
                  <tr>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">ID</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">User</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Word</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Translation</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Grammar</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Language</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Seen</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Source</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Last Practiced</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Created</th>
                    <th className="px-2 py-3 sm:px-4 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <WordBankTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <WordBankTable data={data ?? []} />
          )}

          <Pagination
            numberOfPages={numberOfPages}
            currentPage={currentPage}
            prev={prev}
            next={next}
            isLoading={isLoading || isFetching}
          />
        </>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Word Bank</h3>
          <p className="text-red-600">{errorMessage}</p>
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default WordBankPage;
